import {
  Button,
  Flex,
  Heading,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  IconXLine,
  Spinner,
  Text,
  TextInput,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../../components/CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../components/DialogYesNo/DialogYesNo";
import { InputDate } from "../../../components/InputDate";
import { ExternalService } from "../../../services/ExternalService";
import { Files } from "../../../services/FilesService";
import { buildGoalTreeFromMeasures } from "../../../utils/buildGoalTreeFromMeasures";
import { TermsContext, UserDetailContext } from "../../../utils/Context";
import GroupByToggle from "../GroupByToggle";
import {
  groupItemsByGoalObjective,
  groupItemsBySection,
} from "../utils/groupingUtils";
import AgendaItem from "./AgendaItem";
import "./MeetingAgenda.print.css";
import { printAgenda } from "./printAgendaUtils";
import PublishedAgenda from "./PublishedAgenda";
import { buildPlanLookups, resolveTermStatuses } from "./utils";

const MeetingAgenda = ({
  plan,
  agenda,
  setAgenda,
  groupBy,
  setGroupBy,
  initialAgenda,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { insightsTerms: contextInsightsTerms, canvasTerms } =
    useContext(TermsContext);
  const location = useLocation();
  const userDetails = useContext(UserDetailContext);
  const [publishedAgendas, setPublishedAgendas] = useState([]);
  const [loadingPublished, setLoadingPublished] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [externalInsightsTerms, setExternalInsightsTerms] = useState([]);
  const [editingOriginalFileName, setEditingOriginalFileName] = useState(null);
  const [cancelEditConfirm, setCancelEditConfirm] = useState(false);
  const agendaNameRef = useRef();

  const userEmail =
    userDetails.email || location.state?.resultsState.user || "";
  const planId = plan?.ddl_id || plan?.id;
  const fileService = location.state?.resultsState ? ExternalService : Files;
  const effectiveGroupBy = plan?.include_goals_objs ? groupBy : "section";
  const insightsTerms =
    contextInsightsTerms && contextInsightsTerms.length > 0
      ? contextInsightsTerms
      : externalInsightsTerms;

  // Keep group_order.mode in sync with effectiveGroupBy (handles cross-tab toggle changes)
  useEffect(() => {
    if (agenda && agenda.group_order?.mode !== effectiveGroupBy) {
      setAgenda((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          group_order: {
            ...prev.group_order,
            mode: effectiveGroupBy,
            order: [],
          },
        };
      });
    }
  }, [effectiveGroupBy, agenda, setAgenda]);

  // For external access, fetch insights terms if context is empty
  useEffect(() => {
    if (
      (!contextInsightsTerms || contextInsightsTerms.length === 0) &&
      planId
    ) {
      const fetchTerms = async () => {
        try {
          const terms = await ExternalService.getInsightsTerms();
          setExternalInsightsTerms(terms || []);
        } catch {
          setExternalInsightsTerms([]);
        }
      };
      fetchTerms();
    }
  }, [contextInsightsTerms, planId]);

  // Build lookup maps from plan for hydration
  const { assessmentMap, qualitativeMeasureMap } = useMemo(
    () => buildPlanLookups(plan),
    [plan]
  );

  // Fetch published agendas list via reviewFiles — metadata parsed from filename
  useEffect(() => {
    if (!planId) {
      setLoadingPublished(false);
      return;
    }
    let cancelled = false;

    const fetchPublished = async () => {
      try {
        const files = await fileService.reviewFiles("agenda", planId);
        if (!cancelled && Array.isArray(files)) {
          // Sort by last_modified descending (most recent first)
          const sorted = files.sort(
            (a, b) =>
              new Date(b.last_modified || 0) - new Date(a.last_modified || 0)
          );
          // Parse metadata from filename:
          // New 5-segment: {name}--{meeting_date}--{item_count}--{author_email}--{timestamp}.json
          // Old 4-segment: {name}--{meeting_date}--{item_count}--{timestamp}.json
          const enriched = sorted.map((entry) => {
            const fname = (entry.file_name || "").replace(/\.json$/, "");
            const parts = fname.split("--");
            const hasAuthor = parts.length >= 5;
            return {
              ...entry,
              agenda_name: parts.length >= 4 ? parts[0].replace(/_/g, " ") : "",
              agenda_meeting_date: parts.length >= 4 ? parts[1] : "",
              agenda_item_count:
                parts.length >= 4 ? parseInt(parts[2], 10) || 0 : 0,
              updated_by: hasAuthor ? parts[3].replace(/_at_/g, "@") : "",
            };
          });
          if (!cancelled) setPublishedAgendas(enriched);
        }
      } catch {
        if (!cancelled) setPublishedAgendas([]);
      } finally {
        if (!cancelled) setLoadingPublished(false);
      }
    };

    fetchPublished();
    return () => {
      cancelled = true;
    };
  }, [planId, fileService]); // eslint-disable-line

  // Normalize agenda items to have camelCase fields for grouping utils
  const normalizedItems = useMemo(() => {
    return agenda.items.map((item) => ({
      ...item,
      sectionName: item.section_name || "",
      goalId: item.goal_id || null,
      objectiveId: item.objective_id || null,
    }));
  }, [agenda.items]);

  // Build goals tree for goal/objective grouping
  const goals = useMemo(() => {
    if (effectiveGroupBy !== "goalObjective") return [];
    const measures = normalizedItems
      .filter((item) => item.goalId)
      .map((item) => ({
        goal: {
          id: item.goalId,
          description: item.goal_description || item.goalId,
        },
        objective: item.objectiveId
          ? {
              id: item.objectiveId,
              description: item.objective_description || item.objectiveId,
            }
          : null,
      }));
    return buildGoalTreeFromMeasures(measures);
  }, [normalizedItems, effectiveGroupBy]);

  // Group agenda items based on effectiveGroupBy mode
  const groupedItems = useMemo(() => {
    if (normalizedItems.length === 0) return [];
    const sorted = [...normalizedItems].sort((a, b) => a.sequence - b.sequence);
    if (effectiveGroupBy === "goalObjective") {
      return groupItemsByGoalObjective(sorted, goals);
    }
    return groupItemsBySection(sorted);
  }, [normalizedItems, effectiveGroupBy, goals]);

  // Get current group keys from groupedItems for initializing custom order
  const currentGroupKeys = useMemo(() => {
    if (effectiveGroupBy === "section") {
      return groupedItems.map((g) => g.sectionName);
    }
    return groupedItems.map((g) => g.goalId);
  }, [groupedItems, effectiveGroupBy]);

  // Apply custom group order to rendered output
  const orderedGroups = useMemo(() => {
    const customOrder = agenda.group_order.order;
    if (customOrder.length === 0) return groupedItems;

    const getKey = (g) =>
      effectiveGroupBy === "section" ? g.sectionName : g.goalId;
    const orderMap = new Map(customOrder.map((key, idx) => [key, idx]));

    return [...groupedItems].sort((a, b) => {
      const aIdx = orderMap.get(getKey(a)) ?? Infinity;
      const bIdx = orderMap.get(getKey(b)) ?? Infinity;
      return aIdx - bIdx;
    });
  }, [groupedItems, agenda.group_order.order, effectiveGroupBy]);

  // Reset group_order when groupBy mode changes
  const handleGroupByChange = (newValue) => {
    if (newValue !== groupBy) {
      setAgenda((prev) => ({
        ...prev,
        group_order: { mode: newValue, order: [] },
      }));
    }
    setGroupBy(newValue);
  };

  const handleMoveGroupUp = (groupKey) => {
    setAgenda((prev) => {
      const order = [
        ...(prev.group_order?.order?.length
          ? prev.group_order.order
          : currentGroupKeys),
      ];
      const idx = order.indexOf(groupKey);
      if (idx <= 0) return prev;
      [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
      return { ...prev, group_order: { ...prev.group_order, order } };
    });
  };

  const handleMoveGroupDown = (groupKey) => {
    setAgenda((prev) => {
      const order = [
        ...(prev.group_order?.order?.length
          ? prev.group_order.order
          : currentGroupKeys),
      ];
      const idx = order.indexOf(groupKey);
      if (idx < 0 || idx >= order.length - 1) return prev;
      [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
      return { ...prev, group_order: { ...prev.group_order, order } };
    });
  };

  // --- Event Handlers ---

  /**
   * Snapshot hydrated display data onto each agenda item at publish time.
   * This makes the published JSON a self-contained historical record,
   * immune to future plan changes or deletions.
   */
  const snapshotItems = (items) => {
    return items.map((item) => {
      const snapshot = { ...item };

      if (item.source_type === "assessment") {
        let assessment = assessmentMap[item.source_id];
        if (!assessment) {
          const parts = item.source_id.split("=");
          if (parts.length > 1) {
            assessment = assessmentMap[parts[1]] || assessmentMap[parts[0]];
          }
          if (!assessment) {
            const matchKey = Object.keys(assessmentMap).find(
              (key) =>
                key === item.source_id ||
                item.source_id.includes(key) ||
                key.includes(item.source_id)
            );
            if (matchKey) assessment = assessmentMap[matchKey];
          }
        }
        if (assessment) {
          const termStatuses = resolveTermStatuses(
            assessment,
            insightsTerms,
            canvasTerms
          );
          const totalTerms = termStatuses.length;
          const completedTerms = termStatuses.filter((ts) => ts.ended).length;
          snapshot.snapshot = {
            assessment_type: assessment.assessment_type || "",
            term_statuses: termStatuses.map((ts) => ({
              name: ts.name,
              ended: ts.ended,
              isCurrent: ts.isCurrent,
            })),
            total_terms: totalTerms,
            completed_terms: completedTerms,
            progress_percentage:
              totalTerms > 0
                ? Math.round((completedTerms / totalTerms) * 100)
                : 0,
          };
        }
      } else if (item.source_type === "qualitative_measure") {
        let measure = qualitativeMeasureMap[item.source_id];
        if (!measure) {
          const parts = item.source_id.split("=");
          if (parts.length > 1) {
            measure =
              qualitativeMeasureMap[parts[1]] ||
              qualitativeMeasureMap[parts[0]];
          }
          if (!measure) {
            const matchKey = Object.keys(qualitativeMeasureMap).find(
              (key) =>
                key === item.source_id ||
                item.source_id.includes(key) ||
                key.includes(item.source_id)
            );
            if (matchKey) measure = qualitativeMeasureMap[matchKey];
          }
        }
        if (measure) {
          let dueTermName = "";
          if (measure.insights_term_id && insightsTerms) {
            const term = insightsTerms.find(
              (it) => it.id === measure.insights_term_id
            );
            if (term) dueTermName = term.name;
          }
          snapshot.snapshot = {
            status: measure.status || "not_started",
            assignees: measure.assignees || "",
            due_term: dueTermName,
          };
        }
      }

      return snapshot;
    });
  };

  const handlePublish = async () => {
    if (agenda.items.length === 0) return;
    setSaving(true);
    try {
      const snapshotedItems = snapshotItems(agenda.items);
      const agendaToPublish = {
        ...agenda,
        items: snapshotedItems,
        updated_at: new Date().toISOString(),
        updated_by: userEmail,
      };
      // Generate filename encoding metadata for list display (avoids N+1 fetches)
      // Format: {name}--{meeting_date}--{item_count}--{author_email}--{timestamp}.json
      const safeName = agenda.name
        .trim()
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .replace(/\s+/g, "_");
      const meetingDate = agenda.meeting_date;
      const itemCount = snapshotedItems.length;
      const authorEmail = userEmail.replace(/@/g, "_at_");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `${safeName}--${meetingDate}--${itemCount}--${authorEmail}--${timestamp}.json`;
      const blob = new File([JSON.stringify(agendaToPublish)], filename, {
        type: "application/json",
      });

      // If editing a previously published agenda, delete the original first (validates authorization)
      // Deleted agendas can be recovered from S3
      if (editingOriginalFileName) {
        try {
          await fileService.deleteAgendaFile(editingOriginalFileName, planId);
        } catch (err) {
          if (err?.status === 403) {
            showNotification(
              t("assessmentPlanningDashboard.meetingAgenda.unauthorized"),
              ERROR
            );
          } else {
            showNotification(
              t(
                "assessmentPlanningDashboard.meetingAgenda.deleteOriginalError"
              ),
              ERROR
            );
          }
          setSaving(false);
          return;
        }
      }

      await fileService.saveFile(blob, "agenda", planId);
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.publishSuccess"),
        SUCCESS
      );

      // If editing, remove old agenda from published list and clear edit state
      if (editingOriginalFileName) {
        setPublishedAgendas((prev) =>
          prev.filter((a) => a.file_name !== editingOriginalFileName)
        );
        setEditingOriginalFileName(null);
      }

      // Clear local builder for next meeting
      setAgenda(initialAgenda);
      // Optimistically add the new agenda to published list (avoids S3 eventual consistency delay)
      const optimisticEntry = {
        file_name: filename,
        last_modified: new Date().toISOString(),
        agenda_name: agenda.name.trim(),
        agenda_meeting_date: meetingDate,
        agenda_item_count: itemCount,
        updated_by: userEmail,
      };
      setPublishedAgendas((prev) => [optimisticEntry, ...prev]);
    } catch {
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.publishError"),
        ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgenda = async (agenda) => {
    try {
      await fileService.deleteAgendaFile(agenda.file_name, planId);
      setPublishedAgendas((prev) =>
        prev.filter((a) => a.file_name !== agenda.file_name)
      );
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.deleteSuccess"),
        SUCCESS
      );
    } catch (err) {
      if (err?.status === 404) {
        setPublishedAgendas((prev) =>
          prev.filter((a) => a.file_name !== agenda.file_name)
        );
        showNotification(
          t("assessmentPlanningDashboard.meetingAgenda.alreadyDeleted"),
          SUCCESS
        );
      } else if (err?.status === 403) {
        showNotification(
          t("assessmentPlanningDashboard.meetingAgenda.unauthorized"),
          ERROR
        );
      } else {
        showNotification(
          t("assessmentPlanningDashboard.meetingAgenda.deleteError"),
          ERROR
        );
      }
    }
  };

  const handleEditAgenda = async (agenda) => {
    try {
      const response = await fileService.getFileContent(
        agenda.file_name,
        "agenda",
        planId
      );
      const parsed =
        typeof response.content === "string"
          ? JSON.parse(response.content)
          : response.content;
      setAgenda({ ...parsed, plan_id: planId });
      setGroupBy(parsed.group_order.mode);
      setEditingOriginalFileName(agenda.file_name);
      agendaNameRef.current.focus();
      agendaNameRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } catch {
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.editLoadError"),
        ERROR
      );
    }
  };

  function handleCancelEditAgenda() {
    setEditingOriginalFileName(null);
    setAgenda(initialAgenda);
    setGroupBy("section");
  }

  const handleClear = () => {
    setShowClearConfirm(true);
  };

  const confirmClear = () => {
    setAgenda((prev) => ({ ...prev, items: [] }));
    setShowClearConfirm(false);
  };

  const handleMeetingDateChange = (date) => {
    setAgenda((prev) => ({
      ...prev,
      meeting_date: date.toLocaleDateString("en-CA"),
    }));
  };

  const handleNameChange = (_e, value) => {
    setAgenda((prev) => ({ ...prev, name: value }));
  };

  const handlePrintInProgress = () => {
    if (agenda.items.length === 0) return;
    const snapshotedItems = snapshotItems(agenda.items);
    printAgenda({ ...agenda, items: snapshotedItems });
  };

  return (
    <View as="div" padding="small 0">
      {/* Group By Toggle */}
      {plan?.include_goals_objs && (
        <View as="div" margin="0 0 small 0">
          <GroupByToggle value={groupBy} onChange={handleGroupByChange} />
        </View>
      )}

      {/* Agenda Builder Header */}
      <View as="div" margin="0 0 medium 0">
        <Flex
          justifyItems="space-between"
          alignItems="end"
          wrap="wrap"
          gap="small"
        >
          <Flex.Item shouldGrow shouldShrink>
            <Flex gap="small" wrap="wrap" alignItems="end">
              <Flex.Item>
                <TextInput
                  renderLabel={
                    <>
                      {t("assessmentPlanningDashboard.meetingAgenda.nameLabel")}
                      <span style={{ color: "#d32f2f" }}> *</span>
                    </>
                  }
                  id="agenda-name"
                  inputRef={(el) => {
                    agendaNameRef.current = el;
                  }}
                  value={agenda.name}
                  onChange={handleNameChange}
                  placeholder={t(
                    "assessmentPlanningDashboard.meetingAgenda.namePlaceholder"
                  )}
                  width="220px"
                />
              </Flex.Item>
              <Flex.Item>
                <InputDate
                  id="agenda-meeting-date"
                  label={t(
                    "assessmentPlanningDashboard.meetingAgenda.meetingDateLabel"
                  )}
                  selectedDate={`${agenda.meeting_date}T00:00:00`}
                  selectHandler={handleMeetingDateChange}
                />
              </Flex.Item>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex gap="small" className="no-print">
              <Flex.Item>
                <Button
                  size="small"
                  color="primary"
                  onClick={handlePublish}
                  interaction={
                    agenda.items.length > 0 &&
                    !saving &&
                    agenda.name.trim().length > 0
                      ? "enabled"
                      : "disabled"
                  }
                >
                  {saving
                    ? t("global.saving")
                    : editingOriginalFileName
                      ? t("assessmentPlanningDashboard.meetingAgenda.republish")
                      : t("assessmentPlanningDashboard.meetingAgenda.publish")}
                </Button>
              </Flex.Item>
              {editingOriginalFileName && (
                <Flex.Item>
                  <Button
                    size="small"
                    onClick={() => setCancelEditConfirm(true)}
                    renderIcon={IconXLine}
                  >
                    {t("assessmentPlanningDashboard.meetingAgenda.cancelEdit")}
                  </Button>
                </Flex.Item>
              )}
              <Flex.Item>
                <Button
                  size="small"
                  onClick={handlePrintInProgress}
                  interaction={agenda.items.length > 0 ? "enabled" : "disabled"}
                >
                  {t("assessmentPlanningDashboard.meetingAgenda.print")}
                </Button>
              </Flex.Item>
              <Flex.Item>
                <Button
                  size="small"
                  onClick={handleClear}
                  interaction={agenda.items.length > 0 ? "enabled" : "disabled"}
                >
                  {t("assessmentPlanningDashboard.meetingAgenda.clear")}
                </Button>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
      </View>

      {/* Agenda Item List */}
      {agenda.items.length === 0 ? (
        <View as="div" padding="large" textAlign="center">
          <Text>
            {t("assessmentPlanningDashboard.meetingAgenda.emptyState")}
          </Text>
        </View>
      ) : (
        <View
          as="div"
          margin="0 0 medium 0"
          role="list"
          aria-label={t(
            "assessmentPlanningDashboard.meetingAgenda.agendaItemList"
          )}
        >
          {effectiveGroupBy === "section"
            ? orderedGroups.map((group, groupIndex) => (
                <View key={group.sectionName} as="div" margin="0 0 medium 0">
                  <Flex wrap="wrap" gap="small" margin="0 0 small 0">
                    <Flex.Item shouldGrow>
                      <Text
                        weight="bold"
                        size="small"
                        color="secondary"
                        transform="uppercase"
                      >
                        {group.sectionName}
                      </Text>
                    </Flex.Item>
                    <Flex.Item>
                      <IconButton
                        renderIcon={IconArrowOpenUpLine}
                        screenReaderLabel={t(
                          "assessmentPlanningDashboard.meetingAgenda.moveGroupUp",
                          { name: group.sectionName }
                        )}
                        size="small"
                        onClick={() => handleMoveGroupUp(group.sectionName)}
                        disabled={groupIndex === 0}
                        margin="0 small 0 0"
                      />
                      <IconButton
                        renderIcon={IconArrowOpenDownLine}
                        screenReaderLabel={t(
                          "assessmentPlanningDashboard.meetingAgenda.moveGroupDown",
                          { name: group.sectionName }
                        )}
                        size="small"
                        onClick={() => handleMoveGroupDown(group.sectionName)}
                        disabled={groupIndex === orderedGroups.length - 1}
                      />
                    </Flex.Item>
                  </Flex>

                  {group.items.map((item, itemIdx) => (
                    <AgendaItem
                      key={item.item_id}
                      item={item}
                      indexInGroup={itemIdx}
                      groupItems={group.items}
                      assessmentMap={assessmentMap}
                      qualitativeMeasureMap={qualitativeMeasureMap}
                      insightsTerms={insightsTerms}
                      canvasTerms={canvasTerms}
                      setAgenda={setAgenda}
                      resolveTermStatuses={resolveTermStatuses}
                    />
                  ))}
                </View>
              ))
            : orderedGroups.map((goalGroup, groupIndex) => (
                <View key={goalGroup.goalId} as="div" margin="0 0 medium 0">
                  <Flex wrap="wrap" gap="small">
                    <Flex.Item shouldGrow>
                      <Heading level="h4">{goalGroup.goalDescription}</Heading>
                    </Flex.Item>
                    <Flex.Item>
                      <IconButton
                        renderIcon={IconArrowOpenUpLine}
                        screenReaderLabel={t(
                          "assessmentPlanningDashboard.meetingAgenda.moveGroupUp",
                          { name: goalGroup.goalDescription }
                        )}
                        size="small"
                        onClick={() => handleMoveGroupUp(goalGroup.goalId)}
                        disabled={groupIndex === 0}
                        margin="0 small 0 0"
                      />
                      <IconButton
                        renderIcon={IconArrowOpenDownLine}
                        screenReaderLabel={t(
                          "assessmentPlanningDashboard.meetingAgenda.moveGroupDown",
                          { name: goalGroup.goalDescription }
                        )}
                        size="small"
                        onClick={() => handleMoveGroupDown(goalGroup.goalId)}
                        disabled={groupIndex === orderedGroups.length - 1}
                      />
                    </Flex.Item>
                  </Flex>
                  {goalGroup.objectiveGroups.map((objGroup) => (
                    <View
                      key={objGroup.objectiveId}
                      as="div"
                      margin="x-small 0 small medium"
                    >
                      <View as="div" margin="0 0 x-small 0">
                        <Text weight="bold" size="small" color="secondary">
                          {objGroup.objectiveDescription}
                        </Text>
                      </View>
                      {objGroup.items.map((item, itemIdx) => (
                        <AgendaItem
                          key={item.item_id}
                          item={item}
                          indexInGroup={itemIdx}
                          groupItems={objGroup.items}
                          assessmentMap={assessmentMap}
                          qualitativeMeasureMap={qualitativeMeasureMap}
                          insightsTerms={insightsTerms}
                          canvasTerms={canvasTerms}
                          setAgenda={setAgenda}
                          resolveTermStatuses={resolveTermStatuses}
                        />
                      ))}
                    </View>
                  ))}
                </View>
              ))}
        </View>
      )}
      {loadingPublished ? (
        <View as="div" textAlign="center">
          <Spinner
            renderTitle={t(
              "assessmentPlanningDashboard.meetingAgenda.loadingPublished"
            )}
          />
        </View>
      ) : (
        publishedAgendas.length > 0 && (
          <View as="div" margin="large 0 0 0" className="no-print">
            <Text
              weight="bold"
              size="small"
              transform="uppercase"
              letterSpacing="expanded"
            >
              {t(
                "assessmentPlanningDashboard.meetingAgenda.publishedAgendaHistory"
              )}
            </Text>
            <Flex wrap="wrap" gap="small" margin="small 0 0 0">
              {publishedAgendas.map((agenda) => (
                <Flex.Item key={agenda.file_name} size="100%">
                  <PublishedAgenda
                    agenda={agenda}
                    planId={planId}
                    fileService={fileService}
                    onDelete={handleDeleteAgenda}
                    onEdit={handleEditAgenda}
                    onEditCancel={handleCancelEditAgenda}
                    editingOriginalFileName={editingOriginalFileName}
                  />
                </Flex.Item>
              ))}
            </Flex>
          </View>
        )
      )}

      <DialogYesNo
        message={t(
          "assessmentPlanningDashboard.meetingAgenda.clearConfirmation"
        )}
        openStatus={showClearConfirm}
        onClickYes={confirmClear}
        onClickNo={() => setShowClearConfirm(false)}
      />
      <DialogYesNo
        openStatus={cancelEditConfirm}
        message={t(
          "assessmentPlanningDashboard.meetingAgenda.cancelEditConfirm"
        )}
        onClickYes={() => {
          handleCancelEditAgenda();
          setCancelEditConfirm(false);
        }}
        onClickNo={() => setCancelEditConfirm(false)}
      />
    </View>
  );
};

export default MeetingAgenda;
